/**
 * DocumentTemplatesList - Global document template management list page
 *
 * Features:
 * - Grid of document template cards
 * - Category and visibility filters
 * - Search
 * - Create template button
 * - Preview modal
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { cn, Button } from '@respectly/ui';
import { Plus, FileText, RefreshCw } from 'lucide-react';
import {
  useDocumentTemplatesStore,
  useActiveDocumentFiltersCount,
} from '../../stores/useDocumentTemplatesStore';
import {
  TemplateCardGrid,
  TemplateFilters,
  TemplatePreview,
} from '../../components/templates';
import type { GlobalTemplate } from '@respectly/shared-types';

export function DocumentTemplatesList() {
  const navigate = useNavigate();
  const [previewTemplate, setPreviewTemplate] = useState<GlobalTemplate | null>(null);

  const {
    templates,
    filters,
    isLoadingList,
    loadTemplates,
    setFilter,
    clearFilters,
    duplicateTemplate,
    deleteTemplate,
  } = useDocumentTemplatesStore();

  const activeFiltersCount = useActiveDocumentFiltersCount();

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleCreateTemplate = () => {
    navigate({ to: '/templates/documents/new' });
  };

  const handleEditTemplate = (template: GlobalTemplate) => {
    navigate({ to: '/templates/documents/$id/edit', params: { id: template.id } });
  };

  const handlePreviewTemplate = (template: GlobalTemplate) => {
    setPreviewTemplate(template);
  };

  const handleDuplicateTemplate = async (template: GlobalTemplate) => {
    const newId = await duplicateTemplate(template.id);
    if (newId) {
      // Optionally navigate to the new template
      // navigate({ to: '/templates/documents/$id/edit', params: { id: newId } });
    }
  };

  const handleDeleteTemplate = async (template: GlobalTemplate) => {
    if (confirm(`Are you sure you want to delete "${template.name}"?`)) {
      await deleteTemplate(template.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Document Templates
              </h1>
              <p className="text-sm text-slate-500">
                Manage global document templates for your tenants
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadTemplates()}
              disabled={isLoadingList}
            >
              <RefreshCw className={cn('h-4 w-4', isLoadingList && 'animate-spin')} />
            </Button>
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 lg:px-6 pb-4">
          <TemplateFilters
            filters={filters}
            onFilterChange={setFilter}
            onClearFilters={clearFilters}
            activeFiltersCount={activeFiltersCount}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <TemplateCardGrid
          templates={templates}
          isLoading={isLoadingList}
          onEdit={handleEditTemplate}
          onPreview={handlePreviewTemplate}
          onDuplicate={handleDuplicateTemplate}
          onDelete={handleDeleteTemplate}
          onCreateNew={handleCreateTemplate}
        />
      </div>

      {/* Preview Modal */}
      <TemplatePreview
        template={previewTemplate}
        open={!!previewTemplate}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
      />
    </motion.div>
  );
}
